// Content body for the ProfileModal: messages, loading state, and active tab panel.
'use client';

import { CheckCircle } from 'lucide-react';
import { Spinner } from '@auctogon/ui/web';
import type { StaffNotificationPreferenceDto } from '@auctogon/core-admin/schemas/responses/staff-notification-preferences';
import type { UseFormReturn } from 'react-hook-form';
import type { StaffProfileFormValues } from '@auctogon/core-admin/schemas/staff-profile';
import type { UserProfile, PasswordFields, PasswordVisibility } from './profile-modal-helpers';
import ProfileTabContent from './ProfileTabContent';
import SecurityTabContent from './SecurityTabContent';
import NotificationsTabContent from './NotificationsTabContent';
import Alert from '../../ui/Alert';

/** Props for the ProfileModalBody component. */
export interface ProfileModalBodyProps {
  activeTab: string;
  isLoading: boolean;
  error: string | null;
  success: string | null;
  profile: UserProfile;
  notifications: StaffNotificationPreferenceDto[];
  isLoadingNotifications: boolean;
  notificationsError: string | null;
  passwords: PasswordFields;
  showPasswords: PasswordVisibility;
  isSaving: boolean;
  form: UseFormReturn<StaffProfileFormValues>;
  onToggleNotification: (eventType: string, channel: string, enabled: boolean) => void;
  onPasswordsChange: (updated: PasswordFields) => void;
  onShowPasswordsChange: (updated: PasswordVisibility) => void;
  onChangePassword: () => void;
}

/**
 * Renders the scrollable content area of the profile modal, including
 * error/success banners, a loading spinner, and the active tab's panel.
 * @param props - All state and handlers needed by the tab panels
 */
export default function ProfileModalBody(props: ProfileModalBodyProps) {
  // BUG-2567: the Profile tab's root error (network, 5xx, a server key with no input)
  // comes from the form; field errors render under their inputs, never here.
  const rootError =
    props.activeTab === 'profile' ? props.form.formState.errors.root?.message : undefined;
  const banner = props.error ?? rootError;
  return (
    // Modal's chrome supplies the body gutter and the scroll (BL-2692).
    <div>
      {/* Messages */}
      {banner && (
        <Alert kind="danger" variant="classic" className="mb-4">
          {banner}
        </Alert>
      )}
      {props.success && (
        <Alert kind="success" variant="classic" role="status" className="mb-4" icon={<CheckCircle className="w-4 h-4" aria-hidden />}>
          {props.success}
        </Alert>
      )}

      {props.isLoading ? (
        <div className="flex items-center justify-center py-12 text-primary">
          <Spinner size="md" />
          <span className="ml-3 text-gray-500">Loading profile...</span>
        </div>
      ) : (
        <>
          {props.activeTab === 'profile' && (
            <ProfileTabContent
              profile={props.profile}
              form={props.form}
            />
          )}
          {props.activeTab === 'security' && (
            <SecurityTabContent
              passwords={props.passwords}
              showPasswords={props.showPasswords}
              isSaving={props.isSaving}
              onPasswordsChange={props.onPasswordsChange}
              onShowPasswordsChange={props.onShowPasswordsChange}
              onChangePassword={props.onChangePassword}
            />
          )}
          {props.activeTab === 'notifications' && (
            <NotificationsTabContent
              preferences={props.notifications}
              isLoading={props.isLoadingNotifications}
              error={props.notificationsError}
              onToggle={props.onToggleNotification}
            />
          )}
        </>
      )}
    </div>
  );
}
